import { ROLES, roleToId } from '../../constants/roles'
import { Close as CloseIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useState, useEffect, useCallback } from 'react'

interface CompanyOption {
  readonly id: number
  readonly name: string
}

interface EditUserData {
  readonly id: number
  readonly companyId: number
  readonly firstName: string
  readonly lastName: string
  readonly email: string
  readonly title: string
  readonly phone: string
  readonly role: string
  readonly organization: string
  readonly isPrimaryContact: boolean
}

interface EditUserDialogProps {
  readonly user: EditUserData | null
  readonly onClose: () => void
  readonly companies: CompanyOption[]
  readonly onUpdateUser: (userData: {
    readonly id: number
    readonly firstName: string
    readonly lastName: string
    readonly email: string
    readonly phone: string
    readonly occupation: string
    readonly role_id: number
    readonly companyId: number
    readonly isPrimaryContact: boolean
  }) => Promise<void>
}

const EditUserDialog = ({
  user,
  onClose,
  companies,
  onUpdateUser,
}: EditUserDialogProps) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('user')
  const [companyId, setCompanyId] = useState<number | ''>('')
  const [isPrimaryContact, setIsPrimaryContact] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setEmail(user.email)
      setTitle(user.title)
      setPhone(user.phone)
      setRole(user.role)
      setCompanyId(user.companyId)
      setIsPrimaryContact(user.isPrimaryContact)
    }
  }, [user])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSave = useCallback(() => {
    if (!user || companyId === '') return
    setSubmitting(true)
    void onUpdateUser({
      id: user.id,
      firstName,
      lastName,
      email,
      phone,
      occupation: title,
      role_id: roleToId(role),
      companyId,
      isPrimaryContact,
    })
      .then(() => handleClose())
      .finally(() => setSubmitting(false))
  }, [
    user,
    onUpdateUser,
    firstName,
    lastName,
    email,
    phone,
    title,
    role,
    companyId,
    isPrimaryContact,
    handleClose,
  ])

  const isFormValid =
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    email.trim() !== '' &&
    companyId !== ''

  return (
    <Dialog
      open={user !== null}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#31353D', backgroundImage: 'none' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Edit User
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="edit-user-org-label">Organization *</InputLabel>
            <Select
              labelId="edit-user-org-label"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value as number)}
              label="Organization *"
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name *"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              fullWidth
            />
            <TextField
              label="Last Name *"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
              fullWidth
            />
          </Box>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter job title"
            fullWidth
          />
          <TextField
            label="Email *"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
            fullWidth
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Enter phone number"
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="edit-user-role-label">Role *</InputLabel>
            <Select
              labelId="edit-user-role-label"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              label="Role *"
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={isPrimaryContact}
                onChange={(e) => setIsPrimaryContact(e.target.checked)}
                disabled={user?.isPrimaryContact === true}
              />
            }
            label="Set as Primary Contact"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isFormValid || submitting}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditUserDialog
